import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  Box,
  Container,
  Header,
  SpaceBetween,
  KeyValuePairs,
  CopyToClipboard,
} from '@amzn/awsui-components-console';
import { ResourceNotFoundException } from '@amzn/helianthus-arbiter-client';
import {
  borderRadiusContainer,
  colorBackgroundLayoutPanelContent,
  colorBorderDividerDefault,
  fontFamilyMonospace,
  fontSizeBodyS,
} from '@amzn/awsui-design-tokens-console';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { ErrorState } from '../../components/ErrorState';
import { LoadingState } from '../../components/LoadingState';

/** Format a Document value (free-form JSON) into a readable string. */
function formatContent(content: unknown): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  return JSON.stringify(content, null, 2);
}

export function GuidanceDetailPage() {
  const { '*': pathParam } = useParams();
  const guidancePath = pathParam ?? '';
  const client = useArbiterClient();
  const [content, setContent] = useState<unknown>(null);
  const [resolvedPath, setResolvedPath] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  useDocumentTitle(guidancePath ? `Guidance: ${guidancePath}` : 'Guidance');

  useEffect(() => {
    setContent(null);
    setResolvedPath('');
    setError(null);

    if (!guidancePath) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    client
      .readGuidance({ path: guidancePath })
      .then((response) => {
        if (cancelled) return;
        setResolvedPath(response.path ?? guidancePath);
        setContent(response.content);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [guidancePath, client, retryCount]);

  if (loading) {
    return <LoadingState label="Loading guidance..." />;
  }

  if (error) {
    const is404 = error instanceof ResourceNotFoundException;
    return (
      <ErrorState
        variant={is404 ? 'not-found' : 'api-error'}
        title={is404 ? 'Guidance not found' : 'Error loading guidance'}
        message={
          is404
            ? `The guidance at path "${guidancePath}" could not be found.`
            : error.message
        }
        onRetry={!is404 ? () => setRetryCount((c) => c + 1) : undefined}
        backLabel="Back to Guidance"
        backHref="/guidance"
      />
    );
  }

  if (!guidancePath) {
    return (
      <ErrorState
        variant="not-found"
        title="No path specified"
        message="Please provide a guidance path."
        backLabel="Back to Guidance"
        backHref="/guidance"
      />
    );
  }

  if (content == null) {
    return (
      <ErrorState
        variant="not-found"
        title="No content available"
        message={`The guidance at path "${guidancePath}" has no content.`}
        backLabel="Back to Guidance"
        backHref="/guidance"
      />
    );
  }

  const formattedContent = formatContent(content);

  return (
    <SpaceBetween size="l">
      <Header variant="h1">{resolvedPath || guidancePath}</Header>

      <Container header={<Header variant="h2">Details</Header>}>
        <KeyValuePairs
          items={[
            { label: 'Path', value: resolvedPath || guidancePath },
          ]}
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <CopyToClipboard
                variant="button"
                textToCopy={formattedContent}
                copySuccessText="Content copied"
                copyErrorText="Failed to copy"
                copyButtonText="Copy content"
              />
            }
          >
            Content
          </Header>
        }
      >
        <Box>
          <pre
            style={{
              backgroundColor: colorBackgroundLayoutPanelContent,
              border: `1px solid ${colorBorderDividerDefault}`,
              borderRadius: borderRadiusContainer,
              padding: '12px 16px',
              fontFamily: fontFamilyMonospace,
              fontSize: fontSizeBodyS,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            {formattedContent}
          </pre>
        </Box>
      </Container>
    </SpaceBetween>
  );
}
